import React from 'react'
import { Dialog } from '@mui/material'
import {
  CloseRounded, SendRounded, InfoOutlined, CheckCircleOutline, WarningAmberRounded, ReportOutlined, PersonAddAltOutlined,
  NotificationsActiveOutlined, NotificationsNoneRounded, ChatBubbleOutlineRounded, AutoFixHighRounded, NewReleasesOutlined, ScheduleRounded,
} from '@mui/icons-material'
import { api } from '../../function/axios'
import { ParagraphEditor } from '../../container/marketing/campaign/ParagraphEditor'
import { paragraphText } from '../../container/marketing/campaign/paragraphFormat'
import { Paragraph } from '../../container/marketing/campaign/CampaignPreviewV2'
import { UserPicker, User } from '../package-v2/UserPicker'
import { PackagePicker } from '../../container/marketing/campaign/PackagePicker'
import { Billing, buyActionUrl } from '../../container/marketing/campaign/campaignAction'
import { newSlide, Slide, SlidesCarousel, SlidesEditor, slidesBody, slidesProblem } from './SlidesV2'
import l from '../../page/SubscribersPageV2.module.css'
import s from './MessagesV2.module.css'

/*
 * A new message to users — what, how loud, with an optional button, until
 * when, and to whom (everyone / the users of some packages / chosen users).
 * How many it reaches is asked as the audience changes (POST
 * /admin-messages/estimate); sending asks once more with that number.
 * The text is the campaign paragraph's markup (bold, links, a list…) — the
 * dashboard draws it the same way.
 */

export type Level = 'info' | 'success' | 'warning' | 'critical' | 'update'
export const LEVELS: { key: Level; label: string }[] = [
  { key: 'info', label: 'Info' },
  { key: 'success', label: 'Success' },
  { key: 'warning', label: 'Warning' },
  { key: 'critical', label: 'Critical' },
  { key: 'update', label: 'Updates' },
]
export const LEVEL_TONE: Record<Level, string> = { info: 'var(--primary)', success: 'var(--ok)', warning: 'var(--warn)', critical: 'var(--danger)', update: '#8B5CF6' }
export function LevelIcon({ level, size = 18 }: { level: Level; size?: number }) {
  const sx = { fontSize: size, color: LEVEL_TONE[level] }
  if (level === 'success') return <CheckCircleOutline sx={sx} />
  if (level === 'warning') return <WarningAmberRounded sx={sx} />
  if (level === 'critical') return <ReportOutlined sx={sx} />
  if (level === 'update') return <NewReleasesOutlined sx={sx} />
  return <InfoOutlined sx={sx} />
}

export type Display = 'quiet' | 'bubble'
export const DISPLAYS: { key: Display; label: string; hint: string }[] = [
  { key: 'quiet', label: 'Quiet', hint: 'only in the bell' },
  { key: 'bubble', label: 'Bubble', hint: 'also pops up in the bottom-right corner until closed' },
]

type AudienceType = 'all' | 'packages' | 'users'
type Pkg = {
  id: number; title: string; isActive?: boolean; isFree?: boolean; uid?: string | null; type?: string
  price?: number; productId?: number; annualPrice?: number | null; annualProductId?: number | null
}
/** what the button can sell — as a campaign's (CampaignDialogV2): on sale, paid, for everyone, with a PayPro product */
const isBuyable = (p: Pkg) =>
  !!p.isActive && !p.isFree && !p.uid && !!p.productId && !!p.price && !['custom', 'demo', 'webinthub_demo'].includes(p.type || '')
const hasAnnual = (p?: Pkg | null) => !!p?.annualProductId && !!p?.annualPrice
const EXPIRY: { key: string; label: string; days: number | null }[] = [
  { key: 'never', label: 'No end', days: null }, { key: '1', label: '1 day', days: 1 }, { key: '7', label: '1 week', days: 7 }, { key: '30', label: '1 month', days: 30 },
]
const TITLE_MAX = 80
/** the value of a datetime-local input, in the admin's own time zone */
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
const nextHour = () => { const d = new Date(Date.now() + 3600000); d.setMinutes(0, 0, 0); return localInput(d) }
const whenLabel = (v: string) => new Date(v).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const BODY_MAX = 1000
/* "Fill" — test data, so the form need not be typed in every time */
const SAMPLES = [
  { title: 'Spring sale — 20% off every package', body: 'Our **spring sale** is on until Sunday.\n- 20% off all packages\n- _Credits never expire_\n{center}Your code:\n{center}{code:SPRING20}' },
  { title: 'New lookups are available', body: 'We added ==three new sources== to the search.\nRead more in [our blog](https://espysys.com/blog).\n{center}Try them with {code:NEWLOOKUP}' },
  { title: 'Scheduled maintenance on Sunday', body: 'The service will be unavailable on **Sunday 02:00–03:00 UTC**.\nAs a thank-you for your patience:\n{center}{code:SORRY10}' },
]
const pick = <T,>(xs: T[]): T => xs[Math.floor(Math.random() * xs.length)]
const nameOf = (u: User) => [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email || u.uid || `#${u.id}`

export function ComposeMessageV2({ theme, onClose, onSent, presetUsers }: {
  theme: string
  onClose: () => void
  onSent: () => void
  /** start with these users as the audience (from a subscriber's page) */
  presetUsers?: User[]
}) {
  const [title, setTitle] = React.useState('')
  const [body, setBody] = React.useState('')
  const [level, setLevel] = React.useState<Level>('info')
  const [display, setDisplay] = React.useState<Display>('quiet')
  // an update's carousel (SlidesV2)
  const [slides, setSlides] = React.useState<Slide[]>([newSlide()])
  const [actionName, setActionName] = React.useState('')
  const [actionUrl, setActionUrl] = React.useState('')
  // the button opens a link, or buys a package — then actionUrl is /packages?buy=…, as a campaign's (campaignAction.ts)
  const [actionKind, setActionKind] = React.useState<'link' | 'package'>('link')
  const [buyId, setBuyId] = React.useState<number | null>(null)
  const [billing, setBilling] = React.useState<Billing>('monthly')
  const [expiry, setExpiry] = React.useState('never')
  // send now, or at a time (the backend sends it then — AdminMessagesService#sendDue)
  const [when, setWhen] = React.useState<'now' | 'scheduled'>('now')
  const [at, setAt] = React.useState(nextHour)
  const [type, setType] = React.useState<AudienceType>(presetUsers?.length ? 'users' : 'all')
  const [packages, setPackages] = React.useState<Pkg[] | null>(null)
  const [pkgIds, setPkgIds] = React.useState<number[]>([])
  const [users, setUsers] = React.useState<User[]>(presetUsers || [])
  const [pickerKey, setPickerKey] = React.useState(0)
  const [count, setCount] = React.useState<number | null>(null)
  const [error, setError] = React.useState('')
  const [confirm, setConfirm] = React.useState(false)
  const [sending, setSending] = React.useState(false)

  React.useEffect(() => {
    api({ method: 'GET', url: '/package/admin', params: { offset: 0, limit: 1000 } })
      .then(({ data }) => {
        // the whole package — prices and PayPro products are needed for "Buys a package"
        const list: Pkg[] = Array.isArray(data) ? data : data?.list || []
        setPackages(list.sort((a, b) => Number(!!b.isActive) - Number(!!a.isActive) || a.title.localeCompare(b.title)))
      })
      .catch(() => setPackages([]))
  }, [])

  const audience = React.useMemo(() => (
    type === 'all' ? { type } : type === 'packages' ? { type, packageIds: pkgIds } : { type, userIds: users.map((u) => u.id) }
  ), [type, pkgIds, users])

  // how many it reaches — asked again, a moment after the audience changes
  React.useEffect(() => {
    setCount(null)
    if ((type === 'packages' && !pkgIds.length) || (type === 'users' && !users.length)) { setCount(0); return undefined }
    const t = window.setTimeout(() => {
      api({ method: 'POST', url: '/admin-messages/estimate', body: { audience } })
        .then(({ data }) => setCount(Number(data?.count) || 0))
        .catch(() => setCount(null))
    }, 350)
    return () => window.clearTimeout(t)
  }, [audience, type, pkgIds.length, users.length])

  const visible = paragraphText(body).trim()
  const buyPkg = packages?.find((p) => p.id === buyId) || null
  const finalUrl = actionKind === 'package'
    ? (buyPkg ? buyActionUrl({ packageId: buyPkg.id, billing, webint: (buyPkg.type || '').startsWith('webinthub') }) : '')
    : actionUrl.trim()
  function pickPackage(id: number | null) {
    setBuyId(id)
    if (!hasAnnual(packages?.find((p) => p.id === id))) setBilling('monthly')
  }

  function problem(): string {
    if (!title.trim()) return 'Give the message a title'
    if (!visible) return 'Write the message'
    if (visible.length > BODY_MAX) return `The message is longer than ${BODY_MAX} characters`
    if (body.length > 2000) return 'The message is too long with its formatting'
    if (level === 'update' && slidesProblem(slides)) return slidesProblem(slides)
    if (actionName.trim() && actionKind === 'package' && !buyPkg) return 'Choose the package the button buys'
    if (actionName.trim() && !/^(https?:\/\/|\/)/.test(finalUrl)) return 'The button needs a link — https://… or a dashboard path like /packages'
    if (!actionName.trim() && finalUrl) return 'The button needs a text'
    if (when === 'scheduled' && (!at || new Date(at).getTime() < Date.now() + 2 * 60000)) return 'Choose a time at least a few minutes ahead'
    if (!count) return 'Nobody matches this audience'
    return ''
  }

  async function send() {
    setSending(true)
    setError('')
    const days = EXPIRY.find((e) => e.key === expiry)?.days
    // the end is counted from when it goes out
    const start = when === 'scheduled' ? new Date(at).getTime() : Date.now()
    try {
      await api({
        method: 'POST',
        url: '/admin-messages',
        body: {
          title: title.trim(),
          body: body.trim(),
          level,
          display,
          slides: level === 'update' ? slidesBody(slides) : undefined,
          actionName: actionName.trim() || undefined,
          actionUrl: actionName.trim() ? finalUrl : undefined,
          expiresAt: days ? new Date(start + days * 86400000).toISOString() : undefined,
          scheduledAt: when === 'scheduled' ? new Date(at).toISOString() : undefined,
          audience,
        },
      })
      onSent()
      onClose()
    } catch (e: any) {
      const m = e?.response?.data?.message
      setError(Array.isArray(m) ? m.join('; ') : m || e?.message || 'The message was not sent')
      setConfirm(false)
    } finally {
      setSending(false)
    }
  }

  function fill() {
    const sample = pick(SAMPLES)
    setTitle(sample.title)
    setBody(sample.body)
    const nextLevel = pick(LEVELS).key
    setLevel(nextLevel)
    if (nextLevel === 'update') {
      setSlides([
        { ...newSlide(), title: 'A new look for the reports', text: 'Every report now opens with a **summary** of what was found.' },
        { ...newSlide(), title: 'Faster phone lookups', text: 'Phone searches return up to ==twice as fast==.' },
        { ...newSlide(), title: 'A gift for you', text: '{center}Use the code\n{center}{code:UPDATE10}' },
      ])
    }
    setDisplay(pick(DISPLAYS).key)
    const sellable = (packages || []).filter(isBuyable)
    if (sellable.length && Math.random() < 0.5) {
      setActionKind('package')
      setActionName('Buy now')
      pickPackage(pick(sellable).id)
    } else {
      setActionKind('link')
      setActionName('See packages')
      setActionUrl('/packages')
    }
    setExpiry(pick(EXPIRY).key)
    setError('')
  }

  const reach = count === null ? '…' : count.toLocaleString()
  return (
    <Dialog open onClose={sending ? undefined : onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.topIcon}><NotificationsActiveOutlined sx={{ fontSize: 19 }} /></span>
          <div className={s.topText}><b>New message</b><span>It appears in the users’ bell in the dashboard — at once if they are online</span></div>
          <button type="button" className={s.ghost} onClick={fill} disabled={sending} title="Fill the form with test data (the audience stays as it is)"><AutoFixHighRounded sx={{ fontSize: 16 }} />Fill</button>
          <button type="button" className={s.iconGhost} onClick={onClose} disabled={sending} aria-label="Close"><CloseRounded sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.split}>
          <div className={s.form}>
            <label className={s.field}>
              <span className={s.labelRow}><span className={s.label}>Title</span><span className={s.count}>{title.length} / {TITLE_MAX}</span></span>
              <input className={s.input} value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} placeholder="Scheduled maintenance on Sunday" />
            </label>
            <div className={s.field}>
              <span className={s.labelRow}><span className={s.label}>Message</span><span className={`${s.count} ${visible.length > BODY_MAX ? s.over : ''}`}>{visible.length} / {BODY_MAX}</span></span>
              <ParagraphEditor value={body} onChange={setBody} placeholder="What the users should know — bold, links and lists work" />
            </div>

            <div className={s.field}>
              <span className={s.label}>Type</span>
              <div className={s.levels}>
                {LEVELS.map((x) => (
                  <button key={x.key} type="button" className={`${s.levelCard} ${level === x.key ? s.levelOn : ''}`} style={{ ['--tone' as any]: LEVEL_TONE[x.key] }} onClick={() => setLevel(x.key)}>
                    <LevelIcon level={x.key} /><b>{x.label}</b>
                  </button>
                ))}
              </div>
            </div>

            {level === 'update' && (
              <div className={s.field}>
                <span className={s.label}>Slides <em>the user leafs through them like a carousel — the button is on the last one</em></span>
                <SlidesEditor slides={slides} onChange={setSlides} />
              </div>
            )}

            <div className={s.field}>
              <span className={s.label}>How it appears</span>
              <div className={s.displays}>
                {DISPLAYS.map((x) => (
                  <button key={x.key} type="button" className={`${s.displayCard} ${display === x.key ? s.displayOn : ''}`} onClick={() => setDisplay(x.key)}>
                    {x.key === 'quiet' ? <NotificationsNoneRounded sx={{ fontSize: 18 }} /> : <ChatBubbleOutlineRounded sx={{ fontSize: 17 }} />}
                    <span><b>{x.label}</b><em>{x.hint}</em></span>
                  </button>
                ))}
              </div>
              {level === 'critical' && <span className={s.hint}>Critical messages also show the banner on top of every page.</span>}
            </div>

            <div className={s.field}>
              <span className={s.label}>The button</span>
              <div className={s.chips}>
                <button type="button" className={`${l.chip} ${actionKind === 'link' ? l.chipOn : ''}`} onClick={() => setActionKind('link')}>Opens a link</button>
                <button type="button" className={`${l.chip} ${actionKind === 'package' ? l.chipOn : ''}`} onClick={() => setActionKind('package')}>Buys a package</button>
              </div>
            </div>

            <div className={s.grid2}>
              <label className={s.field}>
                <span className={s.label}>Button text <em>optional</em></span>
                <input className={s.input} value={actionName} maxLength={30} onChange={(e) => setActionName(e.target.value)} placeholder={actionKind === 'package' ? 'Buy now' : 'Top up'} />
              </label>
              {actionKind === 'link' ? (
                <label className={s.field}>
                  <span className={s.label}>Button link</span>
                  <input className={s.input} value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} placeholder="/packages or https://…" disabled={!actionName.trim()} />
                </label>
              ) : (
                <label className={s.field}>
                  <span className={s.label}>Package</span>
                  <select className={s.input} value={buyId ?? ''} onChange={(e) => pickPackage(e.target.value ? Number(e.target.value) : null)} disabled={!packages || !actionName.trim()}>
                    <option value="">{packages ? 'Choose a package…' : 'Loading packages…'}</option>
                    {(packages || []).filter((p) => isBuyable(p) || p.id === buyId).map((p) => (
                      <option key={p.id} value={p.id}>{p.title} — €{p.price}/mo{hasAnnual(p) ? ` · €${p.annualPrice}/yr` : ''}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {actionKind === 'package' && (
              <div className={s.field}>
                <div className={s.chips}>
                  <button type="button" className={`${l.chip} ${billing === 'monthly' ? l.chipOn : ''}`} onClick={() => setBilling('monthly')}>Monthly{buyPkg?.price ? ` · €${buyPkg.price}` : ''}</button>
                  {hasAnnual(buyPkg) && <button type="button" className={`${l.chip} ${billing === 'annual' ? l.chipOn : ''}`} onClick={() => setBilling('annual')}>Annual · €{buyPkg?.annualPrice}</button>}
                </div>
                <span className={s.hint}>The button starts the checkout for this package on the user’s own account, at its current price. If the user cannot buy it there (e.g. a downgrade), the Packages page opens instead.</span>
              </div>
            )}

            <div className={s.field}>
              <span className={s.label}>Send</span>
              <div className={s.chips}>
                <button type="button" className={`${l.chip} ${when === 'now' ? l.chipOn : ''}`} onClick={() => setWhen('now')}>Now</button>
                <button type="button" className={`${l.chip} ${when === 'scheduled' ? l.chipOn : ''}`} onClick={() => setWhen('scheduled')}><ScheduleRounded sx={{ fontSize: 15 }} />Scheduled</button>
                {when === 'scheduled' && (
                  <input type="datetime-local" className={`${s.input} ${s.when}`} value={at} min={localInput(new Date())} onChange={(e) => setAt(e.target.value)} />
                )}
              </div>
              {when === 'scheduled' && <span className={s.hint}>Your time zone. It goes to whoever matches the audience at that moment; it can be cancelled until then.</span>}
            </div>

            <div className={s.field}>
              <span className={s.label}>Shown until{when === 'scheduled' && <em>counted from the send time</em>}</span>
              <div className={s.chips}>
                {EXPIRY.map((x) => <button key={x.key} type="button" className={`${l.chip} ${expiry === x.key ? l.chipOn : ''}`} onClick={() => setExpiry(x.key)}>{x.label}</button>)}
              </div>
            </div>

            <div className={s.field}>
              <span className={s.label}>To</span>
              <div className={s.seg}>
                <button type="button" className={type === 'all' ? s.segOn : ''} onClick={() => setType('all')}>All customers</button>
                <button type="button" className={type === 'packages' ? s.segOn : ''} onClick={() => setType('packages')}>By package</button>
                <button type="button" className={type === 'users' ? s.segOn : ''} onClick={() => setType('users')}>Chosen users</button>
              </div>
              {type === 'packages' && (
                <PackagePicker
                  rules={(packages || []).map((p) => ({
                    key: p.id, title: p.title,
                    inactive: p.isActive === false ? 'Not on sale — users may still have it' : undefined,
                  }))}
                  value={pkgIds}
                  onChange={setPkgIds}
                  loading={packages === null}
                />
              )}
              {type === 'users' && (
                <>
                  <div className={s.picker}>
                    <PersonAddAltOutlined sx={{ fontSize: 17 }} className={s.muted} />
                    <div className={s.pickerBox}>
                      <UserPicker
                        key={pickerKey}
                        value=""
                        onChange={(_uid, u) => {
                          if (u && !users.some((x) => x.id === u.id)) setUsers([...users, u])
                          if (u) setPickerKey((k) => k + 1)
                        }}
                        placeholder="Add a user — name or e-mail"
                      />
                    </div>
                  </div>
                  {!!users.length && (
                    <div className={s.chips}>
                      {users.map((u) => (
                        <span key={u.id} className={s.userChip}>
                          {nameOf(u)}
                          <button type="button" onClick={() => setUsers(users.filter((x) => x.id !== u.id))} aria-label={`Remove ${nameOf(u)}`}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
              <span className={s.reach}>Reaches <b>{reach}</b> {count === 1 ? 'customer' : 'customers'}<em> · deleted and blocked accounts are left out</em></span>
            </div>
          </div>

          <aside className={s.previewPane}>
            {level === 'update' && (
              <>
                <span className={s.paneHead}>The update’s carousel</span>
                <SlidesCarousel slides={slides} actionName={actionName} />
              </>
            )}
            <span className={s.paneHead}>In the user’s bell</span>
            <div className={s.bellItem} style={{ ['--tone' as any]: LEVEL_TONE[level] }}>
              <LevelIcon level={level} size={20} />
              <div className={s.bellText}>
                <b>{title.trim() || 'The title'}</b>
                <div className={s.bellBody}>{visible ? <Paragraph value={body} phone={false} /> : <span className={s.muted}>The message</span>}</div>
                {actionName.trim() && <span className={s.bellBtn}>{actionName.trim()}</span>}
                <span className={`${s.muted} ${s.bellTime}`}>just now</span>
              </div>
              <i className={s.unreadDot} />
            </div>
            {display === 'bubble' && (
              <>
                <span className={s.paneHead}>And in the bottom-right corner</span>
                <div className={s.bubbleScreen}>
                  <div className={s.bubble} style={{ ['--tone' as any]: LEVEL_TONE[level] }}>
                    <LevelIcon level={level} size={18} />
                    <div className={s.bubbleText}>
                      <b>{title.trim() || 'The title'}</b>
                      <span>{visible ? visible.split('\n')[0] : 'The message'}</span>
                      {actionName.trim() && <span className={s.bellBtn}>{actionName.trim()}</span>}
                    </div>
                    <span className={s.bannerX}>×</span>
                  </div>
                </div>
              </>
            )}
            {level === 'critical' && (
              <>
                <span className={s.paneHead}>And on top of every page</span>
                <div className={s.banner}>
                  <LevelIcon level="critical" size={18} />
                  <b>{title.trim() || 'The title'}</b>
                  {actionName.trim() && <span className={s.bannerBtn}>{actionName.trim()}</span>}
                  <span className={s.bannerX}>×</span>
                </div>
              </>
            )}
          </aside>
        </div>

        <div className={s.foot}>
          {error ? <span className={s.err}>{error}</span> : (
            <span className={s.footNote}>
              {problem() || (when === 'scheduled'
                ? `Ready — it goes out ${whenLabel(at)}, to ${reach} ${count === 1 ? 'customer' : 'customers'} as of now`
                : `Ready — it goes to ${reach} ${count === 1 ? 'customer' : 'customers'}`)}
            </span>
          )}
          <button type="button" className={s.ghost} onClick={onClose} disabled={sending}>Cancel</button>
          <button type="button" className={s.primary} onClick={() => setConfirm(true)} disabled={!!problem() || sending}>
            {when === 'scheduled' ? <><ScheduleRounded sx={{ fontSize: 16 }} />Schedule</> : <><SendRounded sx={{ fontSize: 16 }} />Send</>}
          </button>
        </div>

        {confirm && (
          <div className={s.confirm}>
            <div className={s.confirmBox}>
              {when === 'scheduled' ? (
                <>
                  <b>Schedule “{title.trim()}” for {whenLabel(at)}?</b>
                  <span>It goes to whoever matches the audience then — {reach} {count === 1 ? 'customer' : 'customers'} as of now. It can be cancelled until it goes out, but not changed.</span>
                </>
              ) : (
                <>
                  <b>Send “{title.trim()}” to {reach} {count === 1 ? 'customer' : 'customers'}?</b>
                  <span>They see it in the bell right away. It can be withdrawn later, but not changed.</span>
                </>
              )}
              <div className={s.confirmBtns}>
                <button type="button" className={s.ghost} onClick={() => setConfirm(false)} disabled={sending}>Back</button>
                <button type="button" className={s.primary} onClick={() => { void send() }} disabled={sending}>{when === 'scheduled' ? <ScheduleRounded sx={{ fontSize: 16 }} /> : <SendRounded sx={{ fontSize: 16 }} />}{sending ? 'Saving…' : when === 'scheduled' ? 'Schedule' : 'Send'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
